import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API, BACKEND_URL } from "../App";
import { format, parseISO } from "date-fns";
import { pt, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Star, Award, CreditCard, BookOpen, MessageCircle, Calendar, User, Send, ChevronDown, ChevronUp, Loader2, Pencil, Trash2, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "../components/ui/dialog";
import { useTranslation } from "../lib/i18n";

const EVENT_ICONS = {
  review_received: { icon: Star, color: "text-[#F59E0B]", bg: "bg-[#FEF3C7]" },
  ranking_unlocked: { icon: Award, color: "text-[#8B5CF6]", bg: "bg-[#EDE9FE]" },
  credits_added: { icon: CreditCard, color: "text-[#10B981]", bg: "bg-[#D1FAE5]" },
  lesson_completed: { icon: BookOpen, color: "text-[#3B82F6]", bg: "bg-[#DBEAFE]" },
  comment_received: { icon: MessageCircle, color: "text-[#6697a4]", bg: "bg-[#e8eff1]" },
  feedback_received: { icon: MessageCircle, color: "text-[#6697a4]", bg: "bg-[#e8eff1]" },
};

const LogbookEvent = ({ event }) => {
  const config = EVENT_ICONS[event.event_type] || EVENT_ICONS.lesson_completed;
  const Icon = config.icon;
  const { lang } = useTranslation();
  const dateFnsLocale = lang === "en" ? enUS : pt;

  return (
    <div className="flex gap-4" data-testid={`logbook-event-${event.event_id}`}>
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.bg}`}>
          <Icon className={`h-5 w-5 ${config.color}`} />
        </div>
        <div className="w-0.5 flex-1 bg-slate-200 mt-2"></div>
      </div>
      <Card className="flex-1 bg-white border-0 shadow-sm rounded-xl mb-4">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-1">
            <h3 className="font-semibold text-[#3d5a63] text-sm">{event.title}</h3>
            <span className="text-xs text-[#94A3B8] whitespace-nowrap ml-2">
              {event.created_at && format(parseISO(event.created_at), "d MMM", { locale: dateFnsLocale })}
            </span>
          </div>
          <p className="text-sm text-[#475569]">{event.description}</p>
          {event.metadata?.rating && (
            <div className="flex gap-0.5 mt-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`h-3.5 w-3.5 ${s <= event.metadata.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-gray-200"}`} />
              ))}
            </div>
          )}
          {event.metadata?.ranking && (
            <Badge className="mt-2 text-xs" variant="outline">{event.metadata.modality}: {event.metadata.ranking}</Badge>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Reply component with edit/delete for own replies
const ReplyItem = ({ reply, feedbackId, user, onUpdate, onDelete }) => {
  const { lang } = useTranslation();
  const dateFnsLocale = lang === "en" ? enUS : pt;
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(reply.content);
  const [saving, setSaving] = useState(false);

  const canEdit = user && (user.role === "admin" || reply.author_id === user.user_id);

  const handleSave = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/feedback/${feedbackId}/reply/${reply.reply_id}`, { content: editText.trim() }, { withCredentials: true });
      toast.success("Resposta atualizada!");
      setEditing(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(lang === "en" ? "Delete this reply?" : "Apagar esta resposta?")) return;
    try {
      await axios.delete(`${API}/feedback/${feedbackId}/reply/${reply.reply_id}`, { withCredentials: true });
      toast.success("Resposta apagada!");
      onDelete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao apagar");
    }
  };

  return (
    <div
      className={`p-3 rounded-lg ${reply.author_role === "student" ? "bg-[#6697a4]/5 ml-4" : "bg-slate-50 mr-4"}`}
      data-testid={`reply-${reply.reply_id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Avatar className="h-6 w-6">
          <AvatarImage src={reply.author_data?.picture} />
          <AvatarFallback className={`text-[10px] ${reply.author_role === "student" ? "bg-[#6697a4] text-white" : "bg-[#e8927c] text-white"}`}>
            {reply.author_data?.name?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
        <span className="text-xs font-medium text-[#3d5a63]">
          {reply.author_data?.name || (reply.author_role === "student" ? "Aluno" : "Professor")}
        </span>
        <span className="text-[10px] text-[#94A3B8]">
          {reply.created_at && format(parseISO(reply.created_at), "d MMM, HH:mm", { locale: dateFnsLocale })}
        </span>
        {reply.updated_at && <span className="text-[10px] text-[#94A3B8] italic">(editado)</span>}
        {canEdit && !editing && (
          <div className="ml-auto flex gap-1">
            <button onClick={() => { setEditing(true); setEditText(reply.content); }} className="p-1 text-[#94A3B8] hover:text-[#6697a4] rounded" data-testid={`edit-reply-${reply.reply_id}`}>
              <Pencil className="h-3 w-3" />
            </button>
            <button onClick={handleDelete} className="p-1 text-[#94A3B8] hover:text-red-500 rounded" data-testid={`delete-reply-${reply.reply_id}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-1 flex gap-2">
          <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="flex-1 text-sm resize-none" />
          <div className="flex flex-col gap-1">
            <Button size="sm" onClick={handleSave} disabled={saving || !editText.trim()} className="bg-[#6697a4] hover:bg-[#3d5a63] h-7 px-2">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 px-2">
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#475569] whitespace-pre-wrap">{reply.content}</p>
      )}
    </div>
  );
};

// Feedback Card Component with edit/delete + Replies
const FeedbackCard = ({ feedback, onRefresh }) => {
  const { user } = useAuth();
  const { lang } = useTranslation();
  const dateFnsLocale = lang === "en" ? enUS : pt;
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Edit state for the feedback itself
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(feedback.title);
  const [editContent, setEditContent] = useState(feedback.content);
  const [savingEdit, setSavingEdit] = useState(false);

  // Permission checks
  const isAdmin = user?.role === "admin";
  const isInstructor = user?.user_id === feedback.instructor_id;
  const canEditFeedback = isAdmin || isInstructor;

  const fetchReplies = async () => {
    setLoadingReplies(true);
    try {
      const res = await axios.get(`${API}/feedback/${feedback.feedback_id}/replies`, { withCredentials: true });
      setReplies(res.data.replies || []);
    } catch (err) {
      console.error("Failed to fetch replies:", err);
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleToggleReplies = () => {
    if (!showReplies) fetchReplies();
    setShowReplies(!showReplies);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const res = await axios.post(`${API}/feedback/${feedback.feedback_id}/reply`, { content: replyText.trim() }, { withCredentials: true });
      setReplies(prev => [...prev, res.data.reply]);
      setReplyText("");
      toast.success("Resposta enviada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar resposta");
    } finally {
      setSendingReply(false);
    }
  };

  const handleEditFeedback = async () => {
    if (!editTitle.trim() || !editContent.trim()) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/feedback/${feedback.feedback_id}`, { title: editTitle.trim(), content: editContent.trim() }, { withCredentials: true });
      toast.success("Feedback atualizado!");
      setEditing(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteFeedback = async () => {
    if (!window.confirm("Apagar este feedback? Esta ação não pode ser revertida.")) return;
    try {
      await axios.delete(`${API}/feedback/${feedback.feedback_id}`, { withCredentials: true });
      toast.success("Feedback apagado!");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao apagar");
    }
  };

  return (
    <div className="flex gap-3 sm:gap-4" data-testid={`feedback-${feedback.feedback_id}`}>
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#e8eff1] shrink-0">
          <MessageCircle className="h-5 w-5 text-[#6697a4]" />
        </div>
        <div className="w-0.5 flex-1 bg-slate-200 mt-2"></div>
      </div>

      <Card className="flex-1 bg-white border-0 shadow-sm rounded-xl mb-4 min-w-0">
        <CardContent className="p-3 sm:p-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              {editing ? (
                <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="text-sm font-semibold mb-1" data-testid={`edit-feedback-title-${feedback.feedback_id}`} />
              ) : (
                <h3 className="font-semibold text-[#3d5a63] text-sm">{feedback.title}</h3>
              )}
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {feedback.instructor_data && (
                  <span className="text-xs text-[#94A3B8] flex items-center gap-1">
                    <User className="h-3 w-3" /> {feedback.instructor_data.name}
                  </span>
                )}
                {feedback.lesson_data && (
                  <span className="text-xs text-[#94A3B8] flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {feedback.lesson_data.date}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-xs text-[#94A3B8] whitespace-nowrap">
                {feedback.created_at && format(parseISO(feedback.created_at), "d MMM", { locale: dateFnsLocale })}
              </span>
              {feedback.updated_at && <span className="text-[10px] text-[#94A3B8] italic">(editado)</span>}
              {canEditFeedback && !editing && (
                <div className="flex gap-0.5 ml-1">
                  <button onClick={() => { setEditing(true); setEditTitle(feedback.title); setEditContent(feedback.content); }} className="p-1.5 text-[#94A3B8] hover:text-[#6697a4] rounded-lg hover:bg-slate-50" data-testid={`edit-feedback-${feedback.feedback_id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={handleDeleteFeedback} className="p-1.5 text-[#94A3B8] hover:text-red-500 rounded-lg hover:bg-red-50" data-testid={`delete-feedback-${feedback.feedback_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          {editing ? (
            <div className="space-y-2 mt-2">
              <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={3} className="text-sm resize-none" data-testid={`edit-feedback-content-${feedback.feedback_id}`} />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-7 text-xs">Cancelar</Button>
                <Button size="sm" onClick={handleEditFeedback} disabled={savingEdit || !editTitle.trim() || !editContent.trim()} className="bg-[#6697a4] hover:bg-[#3d5a63] h-7 text-xs" data-testid={`save-feedback-${feedback.feedback_id}`}>
                  {savingEdit ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />} Guardar
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Rating if present */}
              {feedback.rating && (
                <div className="flex gap-0.5 mt-2">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`h-4 w-4 ${s <= feedback.rating ? "text-[#F59E0B] fill-current" : "text-gray-200"}`} />
                  ))}
                </div>
              )}
              <p className="text-sm text-[#475569] mt-2 whitespace-pre-wrap">{feedback.content}</p>
              {/* Improvement tips if present */}
              {feedback.improvement_tips && (
                <div className="bg-[#6697a4]/5 p-3 rounded-lg mt-3">
                  <p className="text-xs text-[#6697a4] font-medium mb-1">Dicas de melhoria:</p>
                  <p className="text-xs text-[#475569] whitespace-pre-wrap">{feedback.improvement_tips}</p>
                </div>
              )}
            </>
          )}

          {/* Photos */}
          {!editing && feedback.photos && feedback.photos.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {feedback.photos.map((photo, idx) => (
                <Dialog key={`${feedback.feedback_id}-photo-${idx}`}>
                  <DialogTrigger asChild>
                    <div className="w-20 h-20 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity bg-slate-100">
                      <img src={`${BACKEND_URL}${photo}`} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl p-1 bg-black/90 border-0">
                    <img src={`${BACKEND_URL}${photo}`} alt={`Foto ${idx + 1}`} className="max-h-[80vh] w-auto mx-auto" />
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          )}

          {!editing && feedback.lesson_data && (
            <Badge className="mt-3 text-xs bg-[#6697a4]/10 text-[#6697a4] border-0">
              {feedback.lesson_data.title} - {feedback.lesson_data.date}
            </Badge>
          )}

          {/* Replies Section */}
          <div className="mt-4 pt-3 border-t border-slate-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleReplies}
              className="text-[#6697a4] hover:text-[#3d5a63] hover:bg-[#6697a4]/5 -ml-2"
              data-testid={`toggle-replies-${feedback.feedback_id}`}
            >
              {showReplies ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
              Conversa
              {replies.length > 0 && ` (${replies.length})`}
            </Button>

            {showReplies && (
              <div className="mt-3 space-y-3">
                {loadingReplies && (
                  <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    <Loader2 className="h-4 w-4 animate-spin" /> A carregar...
                  </div>
                )}

                {replies.map((reply) => (
                  <ReplyItem
                    key={reply.reply_id}
                    reply={reply}
                    feedbackId={feedback.feedback_id}
                    user={user}
                    onUpdate={fetchReplies}
                    onDelete={fetchReplies}
                  />
                ))}

                {!loadingReplies && replies.length === 0 && (
                  <p className="text-xs text-[#94A3B8] text-center py-2">
                    Ainda sem respostas. Inicia a conversa!
                  </p>
                )}

                {/* Reply Input */}
                <div className="flex gap-2 mt-3">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Escreve uma resposta..."
                    rows={2}
                    className="flex-1 text-sm resize-none"
                    data-testid={`reply-input-${feedback.feedback_id}`}
                  />
                  <Button
                    size="sm"
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || sendingReply}
                    className="bg-[#6697a4] hover:bg-[#3d5a63] self-end"
                    data-testid={`send-reply-${feedback.feedback_id}`}
                  >
                    {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Logbook = ({ userId }) => {
  const { user } = useAuth();
  const { lang } = useTranslation();
  const [events, setEvents] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const targetUserId = userId || user?.user_id;

  useEffect(() => {
    if (targetUserId) fetchData();
  }, [targetUserId]);

  const fetchData = async () => {
    try {
      const feedbackEndpoint = userId
        ? `${API}/feedback/student/${targetUserId}`
        : `${API}/feedback/my`;

      const [eventsRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/logbook/${targetUserId}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(feedbackEndpoint, { withCredentials: true }).catch(() => ({ data: { feedbacks: [] } }))
      ]);
      setEvents(eventsRes.data || []);
      setFeedbacks(feedbackRes.data?.feedbacks || []);
    } catch {
      // Silently fail for empty logbook
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-8 h-8 border-3 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const combinedItems = [
    ...events.map(e => ({ ...e, type: 'event', sortDate: e.created_at })),
    ...feedbacks.map(f => ({ ...f, type: 'feedback', sortDate: f.created_at }))
  ].sort((a, b) => new Date(b.sortDate) - new Date(a.sortDate));

  if (combinedItems.length === 0) {
    return (
      <div className="text-center py-12" data-testid="logbook-empty">
        <Award className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
        <p className="text-[#475569] font-medium">
          {lang === "en" ? "Your logbook is empty" : "A tua caderneta esta vazia"}
        </p>
        <p className="text-sm text-[#94A3B8]">
          {lang === "en"
            ? "Your achievements, reviews and progress will appear here"
            : "As tuas conquistas, reviews e progressos vao aparecer aqui"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid="logbook-feed">
      <h2 className="text-xl font-bold text-[#3d5a63] mb-6">
        {lang === "en" ? "Logbook" : "Caderneta"}
      </h2>
      {combinedItems.map((item) => (
        item.type === 'feedback'
          ? <FeedbackCard key={item.feedback_id} feedback={item} onRefresh={fetchData} />
          : <LogbookEvent key={item.event_id} event={item} />
      ))}
    </div>
  );
};

export default Logbook;
